import React, { useState, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@material-ui/core'
import Page from '../../components/Page'
import AnalysisTable from '../AnalysisView/AnalysisTable'
import { useOrganizations } from '../../api/pipeline-task-history/list'
import { useSystemInfo } from '../../api/system-info'

const SERVICE_LABELS = {
  frontend: 'Frontend',
  libra: 'Libra (Backend)',
  fastq: 'FASTQ Service',
  vep: 'VEP Service',
  acmg: 'ACMG Service',
  gagi: 'GAGI Service',
  cnv: 'CNV Service',
  genesus_cnv: 'Genesus CNV Service',
  ont: 'ONT Service',
}

const AdminView = () => {
  const { data: organizations, isLoading: isLoadingOrgs } = useOrganizations()
  const { data: systemInfo, isLoading: isLoadingSystemInfo } = useSystemInfo()
  const [selectedOrg, setSelectedOrg] = useState(null)

  const orgOptions = useMemo(() => {
    if (!organizations) return []
    const list = Array.isArray(organizations) ? organizations : organizations.results || []
    return list
  }, [organizations])

  const versionRows = useMemo(() => {
    const frontendVersion = process.env.REACT_APP_VERSION || null
    const rows = [{ service: 'frontend', version: frontendVersion }]
    if (systemInfo) {
      Object.entries(systemInfo).forEach(([service, version]) => {
        rows.push({ service, version })
      })
    }
    return rows
  }, [systemInfo])

  return (
    <Page title="Admin | Lidya Genomics">
      <Container maxWidth={false}>
        <Box sx={{ pb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Admin Dashboard</Typography>
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={orgOptions}
            getOptionLabel={(option) => option.name || option.slug || ''}
            sx={{ width: 300 }}
            value={selectedOrg}
            onChange={(event, newValue) => {
              setSelectedOrg(newValue)
            }}
            renderInput={(params) => <TextField {...params} label="Filter by Organization" />}
            loading={isLoadingOrgs}
          />
        </Box>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Deployed Versions
            </Typography>
            {isLoadingSystemInfo ? (
              <Typography color="textSecondary">Loading...</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Service</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Version</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {versionRows.map(({ service, version }) => (
                      <TableRow key={service}>
                        <TableCell>{SERVICE_LABELS[service] || service}</TableCell>
                        <TableCell>
                          {version ? (
                            <Chip
                              label={version}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontFamily: 'fontFamilyMono' }}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <AnalysisTable organizationId={selectedOrg?.id || selectedOrg?.uuid} />
      </Container>
    </Page>
  )
}

export default AdminView
